/**
 * ConditionErrorBoundary Component
 *
 * Shared error boundary for lazy-loaded condition components.
 * Prevents the entire audit log view from crashing if a condition fails to load.
 */

import { Component, type ReactNode } from 'react';

export interface ConditionErrorBoundaryProps {
  fallback: ReactNode;
  children: ReactNode;
}

interface ConditionErrorBoundaryState {
  hasError: boolean;
}

/**
 * Error boundary to catch errors in lazy-loaded condition fetch components.
 * Prevents the entire audit log view from crashing if a condition fails to load.
 */
export class ConditionErrorBoundary extends Component<ConditionErrorBoundaryProps, ConditionErrorBoundaryState> {
  constructor(props: ConditionErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error: Error): ConditionErrorBoundaryState {
    return { hasError: true };
  }

  override render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

export default ConditionErrorBoundary;
